/** Issue a transmittal — the screen that was missing.
 *
 * The backend has created transmittals since the module was built and the register has
 * read them; there was no way to issue one except the API, which meant the one act that
 * makes a revision current for construction could only be performed by a developer.
 *
 * TWO DELIBERATE CHOICES:
 *  • You pick the NOTION, not a revision id. "Latest" and "latest approved" are different
 *    revisions on a live register, and issuing the wrong one is exactly the mistake that
 *    puts a site team on a superseded sheet. The chosen notion is resolved per document
 *    and the resolved revision code is shown before anything is issued.
 *  • A document whose chosen notion does not exist yet is listed as not-issuable with the
 *    reason, never silently dropped from the selection.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Search, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import { Btn, Field, Input, Panel, Select } from '@/components/nfa/NfaPrimitives';

const NOTIONS = [
  ['latestRevision', 'Latest revision', 'The newest revision, approved or not.'],
  ['latestApprovedRevision', 'Latest approved revision',
    'The newest revision carrying a status code that counts as approved.'],
  ['currentForConstruction', 'Current for construction',
    'Already approved AND already acknowledged by the receiving party. Issuing this one '
    + 're-issues what the site already holds.'],
];

const EdmTransmittalCreate = ({ projectId, onIssued, onCancel }) => {
  const T = useNfaTheme();
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [notion, setNotion] = useState('latestRevision');
  const [form, setForm] = useState({ to_org: '', purpose: 'For construction',
    direction: 'outbound', reference: '', note: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!projectId) return;
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    qs.set('limit', '200');
    api.get(`/edm/projects/${projectId}/documents?${qs}`)
      .then((r) => setDocs(r.data.documents || [])).catch(() => setDocs([]));
  }, [projectId, q]);
  useEffect(() => { const h = setTimeout(load, q ? 300 : 0); return () => clearTimeout(h); },
    [load, q]);

  const resolved = useMemo(() => (docs || [])
    .filter((d) => picked.has(d.document_id))
    .map((d) => ({ doc: d, rev: d[notion] || null })), [docs, picked, notion]);
  const issuable = resolved.filter((r) => r.rev?.revision_id);
  const blocked = resolved.filter((r) => !r.rev?.revision_id);

  const issue = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/edm/projects/${projectId}/transmittals`, {
        ...form, revision_ids: issuable.map((x) => x.rev.revision_id) });
      toast.success(`Transmittal ${r.data.number || ''} issued with ${issuable.length} item(s)`);
      setPicked(new Set());
      onIssued?.(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.error || 'Could not issue the transmittal'));
    } finally { setBusy(false); }
  };

  const toggle = (id) => setPicked((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const spec = NOTIONS.find(([k]) => k === notion);
  return (
    <Panel T={T} testId="edm-transmittal-create" title="Issue a transmittal"
      subtitle="Acknowledgement of this transmittal is what moves a revision to current for construction. Nothing else does."
      right={<>
        <Btn T={T} variant="ghost" onClick={onCancel} testId="edm-tc-cancel">Cancel</Btn>
        <Btn T={T} disabled={busy || !form.to_org || issuable.length === 0}
          onClick={issue} testId="edm-tc-issue">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Issue {issuable.length || ''} item(s)
        </Btn>
      </>}>
      <div className="grid md:grid-cols-5 gap-3">
        <Field T={T} label="To organisation" hint="the receiving party on the transmittal">
          <Input T={T} value={form.to_org} data-testid="edm-tc-org"
            placeholder="e.g. Alpha Constructions JV"
            onChange={(e) => setForm({ ...form, to_org: e.target.value })} />
        </Field>
        <Field T={T} label="Purpose">
          <Select T={T} value={form.purpose} data-testid="edm-tc-purpose"
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}>
            {['For construction', 'For review', 'For information', 'For approval',
              'For tender', 'For record'].map((p) => <option key={p} value={p}>{p}</option>)}
          </Select>
        </Field>
        <Field T={T} label="Direction">
          <Select T={T} value={form.direction} data-testid="edm-tc-direction"
            onChange={(e) => setForm({ ...form, direction: e.target.value })}>
            <option value="outbound">Outbound — we are issuing</option>
            <option value="inbound">Inbound — we received it</option>
          </Select>
        </Field>
        <Field T={T} label="Reference" hint="contract or letter reference, optional">
          <Input T={T} value={form.reference} data-testid="edm-tc-reference"
            onChange={(e) => setForm({ ...form, reference: e.target.value })} />
        </Field>
        <Field T={T} label="Which revision of each document" hint={spec?.[2]}>
          <Select T={T} value={notion} data-testid="edm-tc-notion"
            onChange={(e) => setNotion(e.target.value)}>
            {NOTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </Select>
        </Field>
      </div>

      <div className="mt-3">
        <Field T={T} label="Note" hint="carried on the transmittal, optional">
          <Input T={T} value={form.note} data-testid="edm-tc-note"
            onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </Field>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <div className="relative">
          <Search className="w-3 h-3 absolute left-2.5 top-1/2 -translate-y-1/2"
            style={{ color: T.muted }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="edm-tc-search"
            placeholder="filter documents" className="pl-7 pr-2 py-1.5 rounded-md text-[11px] outline-none w-52"
            style={{ background: T.isDark ? T.surfaceAlt : '#FFF',
              border: `1px solid ${T.border}`, color: T.ink }} />
        </div>
        <span className="text-[10.5px]" style={{ color: T.muted }}
          data-testid="edm-tc-count">
          {picked.size} selected · {issuable.length} issuable
          {blocked.length ? ` · ${blocked.length} with no such revision` : ''}
        </span>
      </div>

      <div className="mt-2 rounded-md max-h-72 overflow-auto"
        style={{ border: `1px solid ${T.border}` }} data-testid="edm-tc-doc-list">
        {!docs && <div className="p-3 text-[11px]" style={{ color: T.muted }}>
          <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> Loading the register…</div>}
        {(docs || []).map((d) => {
          const rev = d[notion];
          const on = picked.has(d.document_id);
          return (
            <label key={d.document_id} data-testid={`edm-tc-doc-${d.document_no}`}
              className="flex items-center gap-2 px-3 py-1.5 text-[11px] cursor-pointer"
              style={{ borderBottom: `1px solid ${T.border}`,
                background: on ? T.tint(T.primary) : 'transparent' }}>
              <input type="checkbox" checked={on} onChange={() => toggle(d.document_id)}
                data-testid={`edm-tc-pick-${d.document_no}`} />
              <span className="font-mono" style={{ color: T.primary }}>{d.document_no}</span>
              <span className="truncate flex-1" style={{ color: T.ink }}>{d.title}</span>
              {rev?.revision_id ? (
                <span className="font-mono text-[10.5px]" style={{ color: T.green }}>
                  {rev.revision_code}{rev.status_code ? ` · ${rev.status_code}` : ''}
                </span>
              ) : (
                <span className="text-[10px]" style={{ color: T.amber }}>
                  no {spec?.[1].toLowerCase()} yet — cannot be issued
                </span>
              )}
            </label>
          );
        })}
      </div>

      {blocked.length > 0 && (
        <div className="mt-2 rounded-md px-2.5 py-2 text-[10.5px]"
          data-testid="edm-tc-blocked"
          style={{ background: T.tint(T.amber), border: `1px solid ${T.tint(T.amber, '44')}`,
            color: T.ink }}>
          {blocked.length} selected document(s) have no {spec?.[1].toLowerCase()} and are NOT
          included: {blocked.map((b) => b.doc.document_no).join(', ')}. They are left out and
          named rather than quietly substituted with a different revision.
        </div>
      )}
    </Panel>
  );
};

export default EdmTransmittalCreate;
